import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PORowEditor.css';
import API_BASE_URL from '../config';

function PORowEditor({ row, onUpdate, onSave }) {
  // State for showing raw JSON modal
  const [showRawData, setShowRawData] = useState(false);

  // Parse customer name initially
  const parseCustomerName = (fullName) => {
    if (!fullName) return { title: '', first_name: '', last_name: '' };

    const titles = ['นาย', 'นาง', 'นางสาว', 'ด.ช.', 'ด.ญ.'];
    let title = '';
    let remaining = fullName.trim();

    // Extract title
    for (const t of titles) {
      if (remaining.startsWith(t)) {
        title = t;
        remaining = remaining.substring(t.length).trim();
        break;
      }
    }

    // Split remaining into first_name and last_name
    const parts = remaining.split(/\s+/);
    const first_name = parts[0] || '';
    const last_name = parts.slice(1).join(' ') || '';

    return { title, first_name, last_name };
  };

  // Helper function to convert Buddhist Era (พ.ศ.) to Christian Era (ค.ศ.)
  const convertBEtoAD = (dateStr) => {
    if (!dateStr) return dateStr;

    // Check if date is in format YYYY-MM-DD
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return dateStr;

    const year = parseInt(match[1]);
    const month = match[2];
    const day = match[3];

    // If year > 2500, assume it's Buddhist Era (พ.ศ.)
    if (year > 2500) {
      const adYear = year - 543;
      return `${adYear}-${month}-${day}`;
    }

    return dateStr;
  };

  // Initialize form data with parsed name and license plate
  const initializeFormData = () => {
    const data = { ...row.mappedData };

    // Parse customer name if not already split
    if (data.customer_name && (!data.title || !data.first_name)) {
      const parsed = parseCustomerName(data.customer_name);
      data.title = data.title || parsed.title;
      data.first_name = data.first_name || parsed.first_name;
      data.last_name = data.last_name || parsed.last_name;

      // Auto-set gender based on title
      if (!data.gender && data.title) {
        data.gender = (data.title === 'นาย' || data.title === 'ด.ช.') ? 0 : 1;
      }
    }

    // Parse mobile number - take first number if multiple (comma-separated)
    if (data.mobile_number && data.mobile_number.includes(',')) {
      data.mobile_number = data.mobile_number.split(',')[0].trim();
    }

    // Parse license plate from raw_license_plate field if needed
    // Example: "1ขผ 9174 กรุงเทพมหานคร" -> reg_number: "1ขผ9174กท"
    if (data.raw_license_plate && !data.reg_number) {
      const parsed = parseLicensePlate(data.raw_license_plate);
      data.reg_number = parsed.reg_number;
    }

    // Convert Buddhist Era dates to Christian Era
    if (data.birth_date) data.birth_date = convertBEtoAD(data.birth_date);
    if (data.purchase_date) data.purchase_date = convertBEtoAD(data.purchase_date);
    if (data.effective_date) data.effective_date = convertBEtoAD(data.effective_date);
    if (data.expiry_date) data.expiry_date = convertBEtoAD(data.expiry_date);

    return data;
  };

  // Province abbreviations for license plates
  const provinceAbbreviations = {
    'กรุงเทพมหานคร': 'กท',
    'กระบี่': 'กบ',
    'กาญจนบุรี': 'กจ',
    'กาฬสินธุ์': 'กส',
    'กำแพงเพชร': 'กพ',
    'ขอนแก่น': 'ขก',
    'จันทบุรี': 'จบ',
    'ฉะเชิงเทรา': 'ฉช',
    'ชลบุรี': 'ชบ',
    'ชัยนาท': 'ชน',
    'ชัยภูมิ': 'ชย',
    'ชุมพร': 'ชพ',
    'เชียงราย': 'ชร',
    'เชียงใหม่': 'ชม',
    'ตรัง': 'ตง',
    'ตราด': 'ตร',
    'ตาก': 'ตก',
    'นครนายก': 'นย',
    'นครปฐม': 'นฐ',
    'นครพนม': 'นพ',
    'นครราชสีมา': 'นม',
    'นครศรีธรรมราช': 'นศ',
    'นครสวรรค์': 'นว',
    'นนทบุรี': 'นบ',
    'นราธิวาส': 'นธ',
    'น่าน': 'นน',
    'บึงกาฬ': 'บก',
    'บุรีรัมย์': 'บร',
    'ปทุมธานี': 'ปท',
    'ประจวบคีรีขันธ์': 'ปข',
    'ปราจีนบุรี': 'ปจ',
    'ปัตตานี': 'ปน',
    'พระนครศรีอยุธยา': 'อย',
    'พะเยา': 'พย',
    'พังงา': 'พง',
    'พัทลุง': 'พท',
    'พิจิตร': 'พจ',
    'พิษณุโลก': 'พล',
    'เพชรบุรี': 'พบ',
    'เพชรบูรณ์': 'พช',
    'แพร่': 'พร',
    'ภูเก็ต': 'ภก',
    'มหาสารคาม': 'มค',
    'มุกดาหาร': 'มห',
    'แม่ฮ่องสอน': 'มส',
    'ยโสธร': 'ยส',
    'ยะลา': 'ยล',
    'ร้อยเอ็ด': 'รอ',
    'ระนอง': 'รน',
    'ระยอง': 'รย',
    'ราชบุรี': 'รบ',
    'ลพบุรี': 'ลบ',
    'ลำปาง': 'ลป',
    'ลำพูน': 'ลพ',
    'เลย': 'ลย',
    'ศรีสะเกษ': 'ศก',
    'สกลนคร': 'สน',
    'สงขลา': 'สข',
    'สตูล': 'สต',
    'สมุทรปราการ': 'สป',
    'สมุทรสงคราม': 'สส',
    'สมุทรสาคร': 'สค',
    'สระแก้ว': 'สก',
    'สระบุรี': 'สบ',
    'สิงห์บุรี': 'สห',
    'สุโขทัย': 'สท',
    'สุพรรณบุรี': 'สพ',
    'สุราษฎร์ธานี': 'สฏ',
    'สุรินทร์': 'สร',
    'หนองคาย': 'นค',
    'หนองบัวลำภู': 'นภ',
    'อ่างทอง': 'อท',
    'อำนาจเจริญ': 'อจ',
    'อุดรธานี': 'อด',
    'อุตรดิตถ์': 'อต',
    'อุทัยธานี': 'อน',
    'อุบลราชธานี': 'อบ'
  };

  // Parse license plate format "1ขผ 9174 กรุงเทพมหานคร" -> reg_number: "1ขผ 9174กท"
  const parseLicensePlate = (plateText) => {
    if (!plateText) return { reg_number: '' };

    const parts = plateText.trim().split(/\s+/);
    if (parts.length >= 3) {
      const plateNumber = parts.slice(0, 2).join(' '); // "1ขผ 9174"
      const province = parts.slice(2).join(' '); // "กรุงเทพมหานคร"
      const provinceAbbr = provinceAbbreviations[province] || province.substring(0, 2);

      return {
        reg_number: plateNumber.replace(/\s+/g, '') + provinceAbbr // "1ขผ9174กท"
      };
    }

    // If already in correct format or incomplete
    return { reg_number: plateText.replace(/\s+/g, '') };
  };

  const [formData, setFormData] = useState(initializeFormData());
  const [editedFields, setEditedFields] = useState(new Set()); // Track edited fields

  // Config data states
  const [geographies, setGeographies] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [subDistricts, setSubDistricts] = useState([]);
  const [carBrands, setCarBrands] = useState([]);
  const [carModels, setCarModels] = useState([]);
  const [carCategories, setCarCategories] = useState([]);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [insuranceCarTypes, setInsuranceCarTypes] = useState([]);
  const [products, setProducts] = useState([]);

  // Products by premium (filtered based on net_price)
  const [productsByPremium, setProductsByPremium] = useState([]);
  const [showDebugProducts, setShowDebugProducts] = useState(false); // Debug state
  const [loadingProductsByPremium, setLoadingProductsByPremium] = useState(false);
  const [premiumLookupDone, setPremiumLookupDone] = useState(false);

  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // Selected values for cascade
  const [selectedGeography, setSelectedGeography] = useState(null);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedCarBrand, setSelectedCarBrand] = useState(formData.car_brand_id);

  // Extract initial values to avoid dependency issues
  const initialCarBrandId = formData.car_brand_id;
  const initialProvinceId = formData.address_province_id;
  const initialDistrictId = formData.address_district_id;
  const initialGeographyId = formData.geography_id;

  // Load initial data
  useEffect(() => {
    console.log('🔄 PORowEditor: Loading initial config data...');
    loadGeographies();
    loadProvinces(); // Load all provinces initially
    loadCarBrands();
    loadCarCategories();
    loadInsuranceCompanies();
    loadInsuranceCarTypes();
    loadProducts();

    // If car_brand_id exists, load models for that brand
    if (initialCarBrandId) {
      console.log('🚗 Loading car models for brand ID:', initialCarBrandId);
      loadCarModels(initialCarBrandId);
    }

    // If geography exists, set selectedGeography
    if (initialGeographyId) {
      console.log('🌍 Setting initial geography ID:', initialGeographyId);
      setSelectedGeography(String(initialGeographyId));
    }

    // If province exists, load districts
    if (initialProvinceId) {
      console.log('📍 Loading districts for province ID:', initialProvinceId);
      setSelectedProvince(initialProvinceId);
    }

    // If district exists, load sub-districts
    if (initialDistrictId) {
      console.log('📍 Loading sub-districts for district ID:', initialDistrictId);
      setSelectedDistrict(initialDistrictId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-fill postcode when district is selected (districts have postcode, sub-districts don't)
  useEffect(() => {
    if (formData.address_district_id && districts.length > 0) {
      console.log('🔍 Looking for postcode for district ID:', formData.address_district_id);
      console.log('📋 Available districts:', districts.length);

      const selectedDistrictData = districts.find(
        d => d.id === parseInt(formData.address_district_id)
      );

      console.log('🎯 Found district:', selectedDistrictData);

      if (selectedDistrictData) {
        const postcode = selectedDistrictData.postcode || selectedDistrictData.zip_code || selectedDistrictData.zipcode;
        console.log('📮 Postcode from district:', postcode);

        if (postcode) {
          console.log('✅ Auto-filling postcode:', postcode);
          handleFieldChange('postcode', postcode);
        } else {
          console.warn('⚠️ No postcode found in district data:', selectedDistrictData);
        }
      } else {
        console.warn('⚠️ District not found in list');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.address_district_id, districts]);

  // Load provinces when geography selected
  useEffect(() => {
    if (selectedGeography) {
      loadProvinces(selectedGeography);
    }
  }, [selectedGeography]);

  // Load districts when province selected
  useEffect(() => {
    if (selectedProvince) {
      loadDistricts(selectedProvince);
    }
  }, [selectedProvince]);

  // Load sub-districts when district selected
  useEffect(() => {
    if (selectedDistrict) {
      loadSubDistricts(selectedDistrict);
    }
  }, [selectedDistrict]);

  // Load car models when car brand selected
  useEffect(() => {
    if (selectedCarBrand) {
      loadCarModels(selectedCarBrand);
    }
  }, [selectedCarBrand]);

  // Load products by premium when net_price is available
  useEffect(() => {
    const netPrice = parseFloat(formData.net_price);
    if (netPrice > 0 && !premiumLookupDone) {
      console.log('💰 Net price detected, loading products by premium:', netPrice);
      loadProductsByPremium(netPrice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.net_price]);

  // API Calls
  const loadGeographies = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/config/geography`);
      if (response.data.success) {
        setGeographies(response.data.data?.data || []);
      }
    } catch (error) {
      console.error('Failed to load geographies:', error);
    }
  };

  const loadProvinces = async (geographyId = null) => {
    try {
      const url = geographyId
        ? `${API_BASE_URL}/api/config/provinces?geography_id=${geographyId}`
        : `${API_BASE_URL}/api/config/provinces`;
      console.log('📥 Loading provinces from:', url);
      const response = await axios.get(url);
      if (response.data.success) {
        let provinceData = response.data.data?.data || [];
        console.log('✅ Loaded provinces:', provinceData.length);

        // Fallback: if filtered by geography but got empty, load all provinces
        if (geographyId && provinceData.length === 0) {
          console.log('⚠️ No provinces for geography, loading all provinces as fallback...');
          const fallbackResponse = await axios.get(`${API_BASE_URL}/api/config/provinces`);
          if (fallbackResponse.data.success) {
            provinceData = fallbackResponse.data.data?.data || [];
            console.log('✅ Fallback loaded provinces:', provinceData.length);
          }
        }

        setProvinces(provinceData);
      }
    } catch (error) {
      console.error('❌ Failed to load provinces:', error);
    }
  };

  const loadDistricts = async (provinceId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/config/districts?province_id=${provinceId}`);
      if (response.data.success) {
        setDistricts(response.data.data?.data || []);
      }
    } catch (error) {
      console.error('Failed to load districts:', error);
    }
  };

  const loadSubDistricts = async (districtId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/config/subdistricts?district_id=${districtId}`);
      if (response.data.success) {
        setSubDistricts(response.data.data?.data || []);
      }
    } catch (error) {
      console.error('Failed to load sub-districts:', error);
    }
  };

  const loadCarBrands = async () => {
    try {
      console.log('📥 Loading car brands...');
      const response = await axios.get(`${API_BASE_URL}/api/config/car-brands`);
      if (response.data.success) {
        const brandData = response.data.data?.data || [];
        console.log('✅ Loaded car brands:', brandData.length);
        setCarBrands(brandData);
      }
    } catch (error) {
      console.error('❌ Failed to load car brands:', error);
    }
  };

  const loadCarModels = async (carBrandId) => {
    try {
      const url = `${API_BASE_URL}/api/config/car-models?car_brand_id=${carBrandId}`;
      console.log('📥 Loading car models from:', url);
      const response = await axios.get(url);
      if (response.data.success) {
        const modelData = response.data.data?.data || [];
        console.log('✅ Loaded car models:', modelData.length, 'for brand:', carBrandId);
        console.log('📋 First few models:', modelData.slice(0, 3));
        setCarModels(modelData);
      } else {
        console.warn('⚠️ Failed to load car models:', response.data);
      }
    } catch (error) {
      console.error('❌ Failed to load car models:', error);
    }
  };

  const loadCarCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/config/car-categories`);
      if (response.data.success) {
        setCarCategories(response.data.data?.data || []);
      }
    } catch (error) {
      console.error('Failed to load car categories:', error);
    }
  };

  const loadInsuranceCompanies = async () => {
    try {
      console.log('📥 Loading insurance companies...');
      const response = await axios.get(`${API_BASE_URL}/api/config/insurance-companies`);
      if (response.data.success) {
        const companyData = response.data.data?.data || [];
        console.log('✅ Loaded insurance companies:', companyData.length);

        // Debug finding ID 63
        const id63 = companyData.find(c => String(c.id) === '63');
        if (id63) {
          console.log('🎯 ID 63 FOUND in loaded list:', id63);
        } else {
          console.warn('❌ ID 63 NOT FOUND in loaded list!');
          console.log('📋 First 10 companies:', companyData.slice(0, 10));
        }

        setInsuranceCompanies(companyData);
      }
    } catch (error) {
      console.error('Failed to load insurance companies:', error);
    }
  };

  const loadInsuranceCarTypes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/config/insurance-car-types`);
      if (response.data.success) {
        setInsuranceCarTypes(response.data.data?.data || []);
      }
    } catch (error) {
      console.error('Failed to load insurance car types:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/config/products`);
      if (response.data.success) {
        setProducts(response.data.data?.data || []);
      }
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  // Load products filtered by premium (เบี้ยจ่ายสุทธิ)
  // Load products by premium (filtered based on net_price)
  const loadProductsByPremium = async (netPrice, forceRefresh = false) => {
    if (!netPrice || netPrice <= 0) {
      console.log('⚠️ No valid net_price to lookup products');
      return;
    }

    setLoadingProductsByPremium(true);
    try {
      console.log(`📦 Loading products for premium: ${netPrice} (refresh: ${forceRefresh})`);
      const response = await axios.get(`${API_BASE_URL}/api/config/products-by-premium`, {
        params: {
          premium_real_price: netPrice,
          refresh: forceRefresh ? 'true' : undefined
        }
      });

      console.log('🔍 API Response:', response.data);

      if (response.data.success) {
        // Handle nested data structure: response.data.data might be an object with {code, message, data}
        // or it might be an array directly
        let productsData = [];

        if (Array.isArray(response.data.data)) {
          // Direct array
          productsData = response.data.data;
        } else if (response.data.data && Array.isArray(response.data.data.data)) {
          // Nested: response.data.data.data
          productsData = response.data.data.data;
        } else if (response.data.data && response.data.data.code) {
          // Error response from AME API (e.g., "ไม่พบข้อมูล")
          console.warn('⚠️ AME API returned error:', response.data.data.message);
          productsData = [];
        }

        console.log(`✅ Found ${productsData.length} products for premium ${netPrice}`);
        console.log('📋 Products data:', productsData);
        setProductsByPremium(productsData);
        console.log('✅ State updated: productsByPremium set to', productsData.length, 'items');
        setPremiumLookupDone(true);
        console.log('✅ State updated: premiumLookupDone set to true');

        // Check for stale cache (Auto-heal)
        // If from cache AND names look like old format (e.g. "3ME" instead of "3MEL...")
        if (!forceRefresh && response.data.fromCache) {
          const hasStaleNames = productsData.some(p => p.name && p.name.match(/^\d+ME$/)); // specific check for plain ME
          if (hasStaleNames) {
            console.log('⚠️ Detected stale/simplified product names in cache, forcing refresh...');
            // Recursive call with forceRefresh=true
            // We need to stop current execution to avoid running the selection logic twice
            await loadProductsByPremium(netPrice, true);
            return;
          }
        }

        // Check if product is already mapped - auto-fetch its details
        if (formData.product_id && !formData.product_details) {
          // Find matching product by ID or name
          const mappedProduct = productsData.find(p =>
            String(p.id) === String(formData.product_id) ||
            p.name === formData.product_name
          );

          if (mappedProduct) {
            console.log('🎯 Auto-selecting mapped product:', mappedProduct.name);
            handleProductSelection(mappedProduct);
          } else {
            console.log('⚠️ Mapped product not found in premium products list');
          }
        }


        // Auto-select logic based on mapped installment
        // 1. Determine target installment from formData (if exists)
        let targetInstallment = null;

        // Try to get from existing product details
        if (formData.product_details && formData.product_details.number_installment) {
          targetInstallment = parseInt(formData.product_details.number_installment, 10);
        }
        // Or try to parse from product name (e.g., "3M" -> 3)
        else if (formData.product_name) {
          const match = formData.product_name.match(/^(\d+)/);
          if (match) {
            targetInstallment = parseInt(match[1], 10);
          }
        }

        // 2. If we have a target installment, find best match (Prioritize ME/MEL)
        if (targetInstallment && !formData.product_id) {
          console.log(`🎯 Target installment detected: ${targetInstallment}`);

          // Find all products with matching installment
          const candidates = productsData.filter(p =>
            parseInt(p.number_installment, 10) === targetInstallment ||
            parseInt(p.total_round, 10) === targetInstallment // backup check
          );

          if (candidates.length > 0) {
            // Prioritize ME or MEL
            const meVariant = candidates.find(p => p.name && p.name.toUpperCase().includes('ME'));

            if (meVariant) {
              console.log('🎯 Auto-selecting ME/MEL variant:', meVariant.name);
              handleProductSelection(meVariant);
            } else {
              console.log('🎯 Auto-selecting first available variant:', candidates[0].name);
              handleProductSelection(candidates[0]);
            }
          }
        }
        // Fallback: Auto-select if only one product available and no product selected
        else if (productsData.length === 1 && !formData.product_id) {
          console.log('🎯 Auto-selecting single available product:', productsData[0].name);
          handleProductSelection(productsData[0]);
        }
      } else {
        console.warn('⚠️ Failed to load products by premium:', response.data.error);
      }
    } catch (error) {
      console.error('❌ Failed to load products by premium:', error);
    } finally {
      // Only clear loading if not recursively calling (or if usage allows parallel)
      setLoadingProductsByPremium(false);
    }
  };

  // Handle product selection - store full product details
  const handleProductSelection = (product) => {
    console.log('📦 Product selected:', product);
    console.log('📦 Product fields available:', Object.keys(product));

    // Check if name contains 'E' (equal payment) or not (30% first payment)
    const hasE = product.name && product.name.toUpperCase().includes('E');
    const paymentType = hasE ? 'หารเท่า' : 'งวดแรก 30%';

    const productDetails = {
      first_payment: product.first_payment,
      number_installment: product.number_installment,
      real_number_installment: product.real_number_installment,
      down_payment_percent: product.down_payment_percent,
      payment_installment: product.payment_installment,
      service_percent: product.service_percent,
      service_price: product.service_price,
      service_other: product.service_other,
      total_first_payment_amount: product.total_first_payment_amount,
      payment_type: paymentType, // หารเท่า or งวดแรก 30%
      premium_used_for_lookup: formData.net_price // Store the premium price used for lookup
    };

    console.log('📦 Setting product_details:', productDetails);

    handleFieldsChange({
      product_id: product.id,
      product_name: product.name,
      product_details: productDetails
    });
  };

  // Validate Thai citizen ID check digit
  const validateCitizenID = (id) => {
    if (!id || id.length !== 13) return false;

    let sum = 0;
    for (let i = 0; i < 12; i++) {
      sum += parseInt(id.charAt(i)) * (13 - i);
    }
    const mod = sum % 11;
    const checkDigit = (11 - mod) % 10;

    return checkDigit === parseInt(id.charAt(12));
  };

  // Compress image using Canvas API
  const compressImage = (file, maxSizeKB = 500, maxWidth = 1920, maxHeight = 1920) => {
    return new Promise((resolve, reject) => {
      // Skip compression for PDF files
      if (file.type === 'application/pdf') {
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Calculate new dimensions maintaining aspect ratio
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }

          // Create canvas and draw resized image
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          // Try different quality levels to achieve target size
          let quality = 0.8;
          const tryCompress = () => {
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('Failed to compress image'));
                  return;
                }

                const sizeKB = blob.size / 1024;
                console.log(`📸 Compressed: ${sizeKB.toFixed(0)}KB (quality: ${quality})`);

                // If still too large and quality can be reduced
                if (sizeKB > maxSizeKB && quality > 0.3) {
                  quality -= 0.1;
                  tryCompress();
                } else {
                  // Create new file with original name
                  const compressedFile = new File([blob], file.name, {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                  });
                  console.log(`✅ Final size: ${(compressedFile.size / 1024).toFixed(0)}KB (original: ${(file.size / 1024).toFixed(0)}KB)`);
                  resolve(compressedFile);
                }
              },
              'image/jpeg',
              quality
            );
          };

          tryCompress();
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  // Handle image upload for insurance document
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('กรุณาเลือกไฟล์รูปภาพ (JPG, PNG) หรือ PDF เท่านั้น');
      return;
    }

    // Validate file size (10MB max for original)
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('ขนาดไฟล์ต้องไม่เกิน 10MB');
      return;
    }

    setUploadingImage(true);
    setUploadError(null);

    try {
      // Compress image if it's larger than 500KB
      let fileToUpload = file;
      if (file.type !== 'application/pdf' && file.size > 500 * 1024) {
        console.log(`📸 Compressing image: ${(file.size / 1024).toFixed(0)}KB`);
        fileToUpload = await compressImage(file, 500, 1920, 1920);
      }

      const formDataUpload = new FormData();
      formDataUpload.append('file', fileToUpload);

      const response = await axios.post(`${API_BASE_URL}/api/po/upload-image`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        console.log('✅ Image uploaded:', response.data.url, 'Filename:', response.data.filename);
        // Store both URL and original filename (for Thai filename display)
        handleFieldsChange({
          insurance_number_url: response.data.url,
          insurance_number_filename: response.data.filename
        });
      } else {
        setUploadError(response.data.error || 'อัพโหลดไม่สำเร็จ');
      }
    } catch (error) {
      console.error('❌ Upload error:', error);
      setUploadError(error.response?.data?.error || error.message || 'เกิดข้อผิดพลาดในการอัพโหลด');
    } finally {
      setUploadingImage(false);
    }
  };

  // Handlers
  const handleFieldChange = (fieldName, value) => {
    setFormData(prevFormData => {
      const updated = { ...prevFormData, [fieldName]: value };

      // Auto-set gender when title changes
      if (fieldName === 'title' && value) {
        updated.gender = (value === 'นาย' || value === 'ด.ช.') ? 0 : 1;
      }

      // Validate and limit citizen ID to 13 digits
      if (fieldName === 'citizen_id') {
        let cleanValue = value.replace(/\D/g, ''); // Remove non-digits
        if (cleanValue.length > 13) cleanValue = cleanValue.substring(0, 13);
        updated.citizen_id = cleanValue;

        // Validate check digit if 13 digits
        if (cleanValue.length === 13 && !validateCitizenID(cleanValue)) {
          console.warn('⚠️ Invalid citizen ID check digit');
        }
      }

      // Parse mobile number - take first if multiple
      if (fieldName === 'mobile_number' && value && value.includes(',')) {
        updated.mobile_number = value.split(',')[0].trim();
      }

      onUpdate(updated);
      return updated;
    });

    // Mark field as edited
    setEditedFields(prev => new Set([...prev, fieldName]));
  };

  // Batch update multiple fields at once (avoids state batching issues)
  const handleFieldsChange = (fieldsObj) => {
    setFormData(prevFormData => {
      const updated = { ...prevFormData, ...fieldsObj };
      onUpdate(updated);
      return updated;
    });

    // Mark all fields as edited
    setEditedFields(prev => new Set([...prev, ...Object.keys(fieldsObj)]));
  };

  // Check if field was edited or has value
  const isFieldComplete = (fieldName) => {
    return editedFields.has(fieldName) || formData[fieldName];
  };

  // Optional fields that don't need orange highlighting when empty
  const optionalFields = ['engine_number', 'phone_number'];

  // Get className for field based on edit status
  // Light orange = needs editing, Light green = completed
  const getFieldClassName = (fieldName) => {
    const value = formData[fieldName];
    // Allow 0 as valid value (for gender = 0 = Male)
    const hasValue = value !== null && value !== undefined && value !== '';
    const wasEdited = editedFields.has(fieldName);

    if (wasEdited || hasValue) {
      return 'field-complete';
    }

    // Optional fields don't show orange when empty
    if (optionalFields.includes(fieldName)) {
      return '';
    }

    return 'needs-edit';
  };

  // Check if all required fields are complete
  const requiredFields = [
    'title', 'first_name', 'last_name', 'citizen_id', 'gender', 'birth_date', 'mobile_number',
    'address', 'address_province_id', 'address_district_id', 'address_sub_district_id', 'postcode',
    'car_brand_id', 'car_model_id', 'car_category_id', 'reg_year', 'car_number', 'reg_number',
    'purchase_date', 'noti_number', 'insurance_company_id', 'insurance_car_type_id', 'product_id',
    'effective_date', 'expiry_date', 'net_price_before', 'net_price', 'price_before_vat',
    'insurance_number', 'insurance_number_url'
  ];

  // CRITICAL FIX: Check for null/undefined/empty, not falsy (to allow 0 for gender)
  const missingFields = requiredFields.filter(field => {
    const value = formData[field];
    // Allow 0 as valid value (for gender = 0 = Male)
    if (value === 0) return false;
    // Check if truly empty
    return value === null || value === undefined || value === '';
  });

  const isRowComplete = missingFields.length === 0;

  const handleGeographyChange = (geographyId) => {
    console.log('🌍 Geography changed to:', geographyId);
    setSelectedGeography(geographyId);
    setSelectedProvince(null);
    setSelectedDistrict(null);
    setDistricts([]);
    setSubDistricts([]);
    // Batch update all address fields at once
    handleFieldsChange({
      address_province_id: '',
      address_district_id: '',
      address_sub_district_id: '',
      postcode: ''
    });
  };

  const handleProvinceChange = (provinceId) => {
    console.log('🏛️ Province changed to:', provinceId);
    setSelectedProvince(provinceId);
    setSelectedDistrict(null);
    setSubDistricts([]);
    // Batch update province and clear children
    handleFieldsChange({
      address_province_id: provinceId,
      address_district_id: '',
      address_sub_district_id: '',
      postcode: ''
    });
  };

  const handleDistrictChange = (districtId) => {
    console.log('🏘️ District changed to:', districtId);
    setSelectedDistrict(districtId);

    // Auto-fill postcode from district data (districts have postcode, sub-districts don't)
    let postcode = '';
    if (districtId && districts.length > 0) {
      const selectedDistrictData = districts.find(d => d.id === parseInt(districtId));
      console.log('🔍 handleDistrictChange - Looking for district:', districtId);
      console.log('🎯 Found district:', selectedDistrictData);

      if (selectedDistrictData) {
        postcode = selectedDistrictData.postcode || selectedDistrictData.zip_code || selectedDistrictData.zipcode || '';
        console.log('📮 Postcode from district:', postcode);
      }
    }

    // Batch update district, clear sub-district, and set postcode
    handleFieldsChange({
      address_district_id: districtId,
      address_sub_district_id: '',
      postcode: postcode
    });
  };

  const handleSubDistrictChange = (subDistrictId) => {
    console.log('🏠 Sub-district changed to:', subDistrictId);
    handleFieldChange('address_sub_district_id', subDistrictId);
  };

  const handleCarBrandChange = (brandId) => {
    console.log('🚗 Car brand changed to:', brandId);
    setSelectedCarBrand(brandId);
    // Batch update brand and clear model
    handleFieldsChange({
      car_brand_id: brandId,
      car_model_id: ''
    });
    // Load models for new brand
    if (brandId) {
      loadCarModels(brandId);
    } else {
      setCarModels([]);
    }
  };

  const handleCarModelChange = (modelId) => {
    console.log('🚙 Car model changed to:', modelId);
    handleFieldChange('car_model_id', modelId);
  };

  // Helper function to get display name for mapped field
  const getFieldDisplayName = (fieldType, id) => {
    switch (fieldType) {
      case 'province':
        return provinces.find(p => String(p.id) === String(id))?.name || '';
      case 'district':
        return districts.find(d => String(d.id) === String(id))?.name || '';
      case 'subdistrict':
        return subDistricts.find(sd => String(sd.id) === String(id))?.name || '';
      case 'car_brand':
        return carBrands.find(b => String(b.id) === String(id))?.name || '';
      case 'car_model':
        return carModels.find(m => String(m.id) === String(id))?.name || '';
      case 'car_category':
        return carCategories.find(c => String(c.id) === String(id))?.name || '';
      case 'insurance_company':
        return insuranceCompanies.find(ic => String(ic.id) === String(id))?.name || `ID: ${id} (Unknown)`;
      case 'insurance_car_type':
        return insuranceCarTypes.find(ict => String(ict.id) === String(id))?.name || '';
      case 'product':
        return products.find(p => String(p.id) === String(id))?.name || '';
      default:
        return '';
    }
  };

  // Self-Healing Effect for Insurance Company
  useEffect(() => {
    // Only run if we have companies loaded and a selected ID
    if (insuranceCompanies.length > 0 && formData.insurance_company_id) {
      // Check if current ID is actually in the list
      const isValidId = insuranceCompanies.some(c => String(c.id) === String(formData.insurance_company_id));

      // If ID is NOT in list (invalid/stale), try to heal it using the name
      if (!isValidId && formData.insurance_company_name) {
        console.log('🚑 Triggering Self-Healing for Insurance Company');

        // Normalize helper: lowercase and remove spaces
        const norm = (s) => String(s || '').toLowerCase().replace(/\s+/g, '');
        const targetName = norm(formData.insurance_company_name);

        // Find match
        const match = insuranceCompanies.find(c => {
          const dbName = norm(c.name);
          return dbName.includes(targetName) || targetName.includes(dbName);
        });

        if (match) {
          console.log(`✅ Healed: "${formData.insurance_company_name}" -> "${match.name}" (ID: ${formData.insurance_company_id} -> ${match.id})`);
          handleFieldChange('insurance_company_id', match.id);
        } else {
          console.log(`❌ Heal failed: Could not find match for "${formData.insurance_company_name}" inside valid list.`);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuranceCompanies, formData.insurance_company_id, formData.insurance_company_name]);

  // Render raw data in a formatted table
  const renderRawDataModal = () => {
    if (!showRawData || !row.originalData) return null;

    const rawData = row.originalData;

    return (
      <div className="raw-data-modal-overlay" onClick={() => setShowRawData(false)}>
        <div className="raw-data-modal" onClick={(e) => e.stopPropagation()}>
          <div className="raw-data-header">
            <h3>📋 Raw Data from ClickBroker API</h3>
            <button className="btn-close-modal" onClick={() => setShowRawData(false)}>✕</button>
          </div>
          <div className="raw-data-content">
            <table className="raw-data-table">
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(rawData).map(([key, value]) => (
                  <tr key={key}>
                    <td className="field-name">{key}</td>
                    <td className="field-value">
                      {value === null || value === undefined
                        ? <span className="null-value">null</span>
                        : typeof value === 'object'
                          ? JSON.stringify(value)
                          : String(value)
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="row-editor">
      <h3>
        Edit Row Details
        {formData.po_number && (
          <span
            className="po-number-link"
            onClick={() => setShowRawData(true)}
            title="Click to view raw API data"
          >
            📄 {formData.po_number}
          </span>
        )}
      </h3>

      {renderRawDataModal()}

      <div className="editor-grid">
        {/* Customer Information */}
        <div className="editor-section">
          <h4>👤 Customer Information</h4>

          <div className="form-fields-grid">
            <div className="form-field">
              <label>Title (คำนำหน้า)</label>
              <select
                className={getFieldClassName('title')}
                value={formData.title || ''}
                onChange={(e) => handleFieldChange('title', e.target.value)}
              >
                <option value="">Select...</option>
                <option value="นาย">นาย</option>
                <option value="นาง">นาง</option>
                <option value="นางสาว">นางสาว</option>
              </select>
            </div>

            <div className="form-field">
              <label>Gender (เพศ) *</label>
              <select
                className={getFieldClassName('gender')}
                value={formData.gender !== undefined ? formData.gender : ''}
                onChange={(e) => handleFieldChange('gender', parseInt(e.target.value))}
              >
                <option value="">Select...</option>
                <option value="0">ชาย (Male)</option>
                <option value="1">หญิง (Female)</option>
              </select>
            </div>

            <div className="form-field">
              <label>First Name (ชื่อ)</label>
              <input
                className={getFieldClassName('first_name')}
                type="text"
                value={formData.first_name || ''}
                onChange={(e) => handleFieldChange('first_name', e.target.value)}
              />
            </div>

            <div className="form-field">
              <label>Last Name (นามสกุล)</label>
              <input
                className={getFieldClassName('last_name')}
                type="text"
                value={formData.last_name || ''}
                onChange={(e) => handleFieldChange('last_name', e.target.value)}
              />
            </div>

            <div className="form-field">
              <label>Citizen ID (เลขบัตรประชาชน)</label>
              <input
                className={getFieldClassName('citizen_id')}
                type="text"
                value={formData.citizen_id || ''}
                onChange={(e) => handleFieldChange('citizen_id', e.target.value)}
                placeholder="1234567890123"
              />
            </div>

            <div className="form-field">
              <label>Birth Date (วันเกิด) *</label>
              <input
                className={getFieldClassName('birth_date')}
                type="date"
                value={formData.birth_date || ''}
                onChange={(e) => handleFieldChange('birth_date', e.target.value)}
              />
            </div>

            <div className="form-field">
              <label>Mobile Number (เบอร์มือถือ) *</label>
              <input
                className={getFieldClassName('mobile_number')}
                type="text"
                value={formData.mobile_number || ''}
                onChange={(e) => handleFieldChange('mobile_number', e.target.value)}
                placeholder="0812345678"
              />
            </div>

            <div className="form-field">
              <label>Phone Number (เบอร์โทร)</label>
              <input
                className={getFieldClassName('phone_number')}
                type="text"
                value={formData.phone_number || ''}
                onChange={(e) => handleFieldChange('phone_number', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Address Information - CASCADE */}
        <div className="editor-section">
          <h4>📍 Address (ที่อยู่)</h4>

          <div className="form-field full-width">
            <label>Address (ที่อยู่)</label>
            <textarea
              className={getFieldClassName('address')}
              value={formData.address || ''}
              onChange={(e) => handleFieldChange('address', e.target.value)}
              rows="2"
            />
          </div>

          <div className="form-fields-grid">
            <div className="form-field">
              <label>Geography (ภูมิภาค)</label>
              <select
                className={selectedGeography ? 'field-complete' : 'needs-edit'}
                value={selectedGeography || ''}
                onChange={(e) => handleGeographyChange(e.target.value)}
              >
                <option value="">Select Geography...</option>
                {geographies.map(g => (
                  <option key={g.id} value={String(g.id)}>{g.name}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Province (จังหวัด) *</label>
              {formData.address_province_id && isFieldComplete('address_province_id') ? (
                <div className="field-mapped">
                  <span className="mapped-badge">✅ {editedFields.has('address_province_id') ? 'Edited' : 'Mapped'}</span>
                  <span className="mapped-value">
                    {getFieldDisplayName('province', formData.address_province_id)}
                  </span>
                </div>
              ) : (
                <div className="field-unmapped">
                  <span className="unmapped-badge">⚠️ Needs Confirmation</span>
                </div>
              )}
              <select
                className={getFieldClassName('address_province_id')}
                value={formData.address_province_id ? String(formData.address_province_id) : ''}
                onChange={(e) => handleProvinceChange(e.target.value)}
                disabled={!selectedGeography && provinces.length === 0}
              >
                <option value="">Select Province...</option>
                {provinces.map(p => (
                  <option key={p.id} value={String(p.id)}>{p.name}</option>
                ))}
              </select>
              {formData.province_name && !formData.address_province_id && (
                <small className="hint">Suggested: {formData.province_name}</small>
              )}
            </div>

            <div className="form-field">
              <label>District (อำเภอ/เขต) *</label>
              {formData.address_district_id && isFieldComplete('address_district_id') ? (
                <div className="field-mapped">
                  <span className="mapped-badge">✅ {editedFields.has('address_district_id') ? 'Edited' : 'Mapped'}</span>
                  <span className="mapped-value">
                    {getFieldDisplayName('district', formData.address_district_id)}
                  </span>
                </div>
              ) : (
                <div className="field-unmapped">
                  <span className="unmapped-badge">⚠️ Needs Confirmation</span>
                </div>
              )}
              <select
                className={getFieldClassName('address_district_id')}
                value={formData.address_district_id ? String(formData.address_district_id) : ''}
                onChange={(e) => handleDistrictChange(e.target.value)}
                disabled={!selectedProvince}
              >
                <option value="">Select District...</option>
                {districts.map(d => (
                  <option key={d.id} value={String(d.id)}>{d.name}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Sub-district (ตำบล/แขวง) *</label>
              {formData.address_sub_district_id && isFieldComplete('address_sub_district_id') ? (
                <div className="field-mapped">
                  <span className="mapped-badge">✅ {editedFields.has('address_sub_district_id') ? 'Edited' : 'Mapped'}</span>
                  <span className="mapped-value">
                    {getFieldDisplayName('subdistrict', formData.address_sub_district_id)}
                  </span>
                </div>
              ) : (
                <div className="field-unmapped">
                  <span className="unmapped-badge">⚠️ Needs Confirmation</span>
                </div>
              )}
              <select
                className={getFieldClassName('address_sub_district_id')}
                value={formData.address_sub_district_id ? String(formData.address_sub_district_id) : ''}
                onChange={(e) => handleSubDistrictChange(e.target.value)}
                disabled={!selectedDistrict}
              >
                <option value="">Select Sub-district...</option>
                {subDistricts.map(sd => (
                  <option key={sd.id} value={String(sd.id)}>{sd.name}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Postcode (รหัสไปรษณีย์)</label>
              <input
                className={getFieldClassName('postcode')}
                type="text"
                value={formData.postcode || ''}
                onChange={(e) => handleFieldChange('postcode', e.target.value)}
                placeholder="10100"
              />
            </div>
          </div>
        </div>

        {/* Car Information - CASCADE */}
        <div className="editor-section">
          <h4>🚗 Car Information (ข้อมูลรถยนต์)</h4>

          <div className="form-fields-grid">
            <div className="form-field">
              <label>Car Brand (ยี่ห้อรถ) *</label>
              {formData.car_brand_id && isFieldComplete('car_brand_id') ? (
                <div className="field-mapped">
                  <span className="mapped-badge">✅ {editedFields.has('car_brand_id') ? 'Edited' : 'Mapped'}</span>
                  <span className="mapped-value">
                    {getFieldDisplayName('car_brand', formData.car_brand_id)}
                  </span>
                </div>
              ) : (
                <div className="field-unmapped">
                  <span className="unmapped-badge">⚠️ Needs Confirmation</span>
                </div>
              )}
              <select
                className={getFieldClassName('car_brand_id')}
                value={formData.car_brand_id ? String(formData.car_brand_id) : ''}
                onChange={(e) => handleCarBrandChange(e.target.value)}
              >
                <option value="">Select Brand...</option>
                {carBrands.map(b => (
                  <option key={b.id} value={String(b.id)}>{b.name}</option>
                ))}
              </select>
              {formData.car_brand_name && !formData.car_brand_id && (
                <small className="hint">Suggested: {formData.car_brand_name}</small>
              )}
            </div>

            <div className="form-field">
              <label>Car Model (รุ่นรถ) *</label>
              {formData.car_model_id && isFieldComplete('car_model_id') ? (
                <div className="field-mapped">
                  <span className="mapped-badge">✅ {editedFields.has('car_model_id') ? 'Edited' : 'Mapped'}</span>
                  <span className="mapped-value">
                    {getFieldDisplayName('car_model', formData.car_model_id)}
                  </span>
                </div>
              ) : (
                <div className="field-unmapped">
                  <span className="unmapped-badge">⚠️ Needs Confirmation</span>
                </div>
              )}
              <select
                className={getFieldClassName('car_model_id')}
                value={formData.car_model_id ? String(formData.car_model_id) : ''}
                onChange={(e) => handleCarModelChange(e.target.value)}
                disabled={!selectedCarBrand && !formData.car_brand_id}
              >
                <option value="">Select Model...</option>
                {carModels.map(m => (
                  <option key={m.id} value={String(m.id)}>{m.name}</option>
                ))}
              </select>
              {formData.car_model_name && !formData.car_model_id && (
                <small className="hint">Suggested: {formData.car_model_name}</small>
              )}
            </div>

            <div className="form-field">
              <label>Car Category (ประเภทรถ) *</label>
              {formData.car_category_id && isFieldComplete('car_category_id') ? (
                <div className="field-mapped">
                  <span className="mapped-badge">✅ {editedFields.has('car_category_id') ? 'Edited' : 'Mapped'}</span>
                  <span className="mapped-value">
                    {getFieldDisplayName('car_category', formData.car_category_id)}
                  </span>
                </div>
              ) : (
                <div className="field-unmapped">
                  <span className="unmapped-badge">⚠️ Needs Confirmation</span>
                </div>
              )}
              <select
                className={getFieldClassName('car_category_id')}
                value={formData.car_category_id ? String(formData.car_category_id) : ''}
                onChange={(e) => handleFieldChange('car_category_id', e.target.value)}
              >
                <option value="">Select Category...</option>
                {carCategories.map(c => (
                  <option key={c.id} value={String(c.id)}>{c.name}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Registration Year (ปีจดทะเบียน) *</label>
              <input
                className={getFieldClassName('reg_year')}
                type="text"
                value={formData.reg_year || ''}
                onChange={(e) => handleFieldChange('reg_year', e.target.value)}
                placeholder="2567"
              />
            </div>

            <div className="form-field">
              <label>License Plate (ทะเบียนรถ) *</label>
              <input
                className={getFieldClassName('reg_number')}
                type="text"
                value={formData.reg_number || ''}
                onChange={(e) => handleFieldChange('reg_number', e.target.value)}
                placeholder="1ขผ9174กท"
              />
              <small className="hint">รูปแบบ: เลขทะเบียน+จังหวัดย่อ เช่น 1ขผ9174กท</small>
            </div>

            <div className="form-field">
              <label>Chassis Number (เลขตัวถัง) *</label>
              <input
                className={getFieldClassName('car_number')}
                type="text"
                value={formData.car_number || ''}
                onChange={(e) => handleFieldChange('car_number', e.target.value)}
                placeholder="MHFXW1820FK123456"
              />
            </div>

            <div className="form-field">
              <label>Engine Number (เลขเครื่องยนต์)</label>
              <input
                className={getFieldClassName('engine_number')}
                type="text"
                value={formData.engine_number || ''}
                onChange={(e) => handleFieldChange('engine_number', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Insurance Information */}
        <div className="editor-section">
          <h4>🏢 Insurance Information (ข้อมูลประกัน)</h4>

          <div className="form-fields-grid">
            <div className="form-field">
              <label>Purchase Date (วันที่สั่งซื้อ) *</label>
              <input
                className={getFieldClassName('purchase_date')}
                type="date"
                value={formData.purchase_date || ''}
                onChange={(e) => handleFieldChange('purchase_date', e.target.value)}
              />
            </div>

            <div className="form-field">
              <label>Noti Number (เลขที่รับแจ้ง) *</label>
              <input
                className={getFieldClassName('noti_number')}
                type="text"
                value={formData.noti_number || ''}
                onChange={(e) => handleFieldChange('noti_number', e.target.value)}
                placeholder="PO-2024-001"
              />
            </div>

            <div className="form-field">
              <label>Insurance Company (บริษัทประกัน) *</label>
              {formData.insurance_company_id && isFieldComplete('insurance_company_id') ? (
                <div className="field-mapped">
                  <span className="mapped-badge">✅ {editedFields.has('insurance_company_id') ? 'Edited' : 'Mapped'}</span>
                  <span className="mapped-value">
                    {getFieldDisplayName('insurance_company', formData.insurance_company_id)}
                  </span>
                </div>
              ) : (
                <div className="field-unmapped">
                  <span className="unmapped-badge">⚠️ Needs Confirmation</span>
                </div>
              )}
              <select
                className={getFieldClassName('insurance_company_id')}
                value={formData.insurance_company_id ? String(formData.insurance_company_id) : ''}
                onChange={(e) => handleFieldChange('insurance_company_id', e.target.value)}
              >
                <option value="">Select Company...</option>
                {insuranceCompanies.map(ic => {
                  if (String(ic.id) === String(formData.insurance_company_id)) {
                    console.log('🎯 Found matching company in list:', ic);
                  }
                  return (
                    <option key={ic.id} value={String(ic.id)}>{ic.name}</option>
                  );
                })}
              </select>
              {(() => {
                console.log('🏢 Mapped ID:', formData.insurance_company_id, 'Type:', typeof formData.insurance_company_id);
                console.log('📋 Company List IDs:', insuranceCompanies.map(c => c.id));
                return null;
              })()}
              {formData.insurance_company_name && !formData.insurance_company_id && (
                <small className="hint">Suggested: {formData.insurance_company_name}</small>
              )}
            </div>

            <div className="form-field">
              <label>Insurance Car Type (ประเภทประกันรถ) *</label>
              {formData.insurance_car_type_id && isFieldComplete('insurance_car_type_id') ? (
                <div className="field-mapped">
                  <span className="mapped-badge">✅ {editedFields.has('insurance_car_type_id') ? 'Edited' : 'Mapped'}</span>
                  <span className="mapped-value">
                    {getFieldDisplayName('insurance_car_type', formData.insurance_car_type_id)}
                  </span>
                </div>
              ) : (
                <div className="field-unmapped">
                  <span className="unmapped-badge">⚠️ Needs Confirmation</span>
                </div>
              )}
              <select
                className={getFieldClassName('insurance_car_type_id')}
                value={formData.insurance_car_type_id ? String(formData.insurance_car_type_id) : ''}
                onChange={(e) => handleFieldChange('insurance_car_type_id', e.target.value)}
              >
                <option value="">Select Type...</option>
                {insuranceCarTypes.map(ict => (
                  <option key={ict.id} value={String(ict.id)}>{ict.name}</option>
                ))}
              </select>
            </div>

            <div className="form-field">
              <label>Insurance Number (เลขกรมธรรม์) <span className="required">*</span></label>
              <input
                className={getFieldClassName('insurance_number')}
                type="text"
                value={formData.insurance_number || ''}
                onChange={(e) => handleFieldChange('insurance_number', e.target.value)}
                placeholder="กรุณากรอกเลขกรมธรรม์"
              />
            </div>

            {/* Image Upload for Insurance Document */}
            <div className="form-field">
              <label>ไฟล์ภาพใบแจ้งงาน/หน้ากรมธรรม์ <span className="required">*</span></label>
              <div className={`upload-container ${!formData.insurance_number_url ? 'needs-upload' : 'upload-complete'}`}>
                <input
                  type="file"
                  id={`insurance-image-${row.rowIndex}`}
                  accept=".jpg,.jpeg,.png,.pdf"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  style={{ display: 'none' }}
                />
                <label
                  htmlFor={`insurance-image-${row.rowIndex}`}
                  className={`btn-upload ${uploadingImage ? 'uploading' : ''} ${!formData.insurance_number_url ? 'needs-file' : ''}`}
                >
                  {uploadingImage ? (
                    <>⏳ กำลังอัพโหลด...</>
                  ) : (
                    <>📷 เลือกไฟล์</>
                  )}
                </label>
                {formData.insurance_number_url && (
                  <div className="upload-success">
                    <span className="success-icon">✅</span>
                    <a
                      href={formData.insurance_number_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="file-link"
                      title={formData.insurance_number_filename || 'ดูไฟล์'}
                    >
                      {formData.insurance_number_filename || 'ดูไฟล์ที่อัพโหลด'}
                    </a>
                    <button
                      type="button"
                      className="btn-remove-file"
                      onClick={() => handleFieldsChange({
                        insurance_number_url: '',
                        insurance_number_filename: ''
                      })}
                      title="ลบไฟล์"
                    >
                      ✕
                    </button>
                  </div>
                )}
                {uploadError && (
                  <div className="upload-error">
                    <span className="error-icon">❌</span>
                    <span>{uploadError}</span>
                  </div>
                )}
              </div>
              <small className="field-hint">รองรับ: JPG, PNG, PDF (รูปภาพจะถูกบีบอัดอัตโนมัติ)</small>
            </div>

            <div className="form-field">
              <label>Effective Date (วันเริ่มคุ้มครอง)</label>
              <input
                className={getFieldClassName('effective_date')}
                type="date"
                value={formData.effective_date || ''}
                onChange={(e) => handleFieldChange('effective_date', e.target.value)}
              />
            </div>

            <div className="form-field">
              <label>Expiry Date (วันสิ้นสุดคุ้มครอง)</label>
              <input
                className={getFieldClassName('expiry_date')}
                type="date"
                value={formData.expiry_date || ''}
                onChange={(e) => handleFieldChange('expiry_date', e.target.value)}
              />
            </div>
          </div>

          <div className="form-field">
            <label>Product (สินค้า) *</label>
            {formData.product_id && isFieldComplete('product_id') ? (
              <div className="field-mapped">
                <span className="mapped-badge">✅ {editedFields.has('product_id') ? 'Edited' : 'Mapped'}</span>
                <span className="mapped-value">
                  {getFieldDisplayName('product', formData.product_id)}
                </span>
              </div>
            ) : (
              <div className="field-unmapped">
                <span className="unmapped-badge">⚠️ Needs Confirmation</span>
              </div>
            )}

            {/* Show products by premium if available */}
            {loadingProductsByPremium && (
              <div className="loading-indicator">🔍 กำลังค้นหาสินค้าตามเบี้ยจ่ายสุทธิ...</div>
            )}

            {/* Debug UI - Hidden by default */}
            {premiumLookupDone && (
              <div style={{ marginTop: '5px' }}>
                <span
                  onClick={() => setShowDebugProducts(!showDebugProducts)}
                  style={{
                    cursor: 'pointer',
                    fontSize: '0.8em',
                    color: '#666',
                    textDecoration: 'underline'
                  }}
                >
                  {showDebugProducts ? '🔽 ซ่อนข้อมูล Debug' : '▶️ แสดงข้อมูล Debug'}
                </span>

                {showDebugProducts && (
                  <div style={{
                    marginTop: '5px',
                    padding: '8px',
                    background: '#f5f5f5',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    <strong>Raw Response ({productsByPremium.length} items):</strong>
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {(() => {
                        try {
                          const displayData = productsByPremium.slice(0, 5);
                          return JSON.stringify(displayData, null, 2);
                        } catch (error) {
                          return `Error displaying JSON: ${error.message}\n\nFallback display:\n${productsByPremium.slice(0, 5).map((p, i) =>
                            `[${i}] ID: ${p.id}, Name: ${p.name}, Installments: ${p.number_installment}`
                          ).join('\n')}`;
                        }
                      })()}
                      {productsByPremium.length > 5 && `\n\n...and ${productsByPremium.length - 5} more items`}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* Debug Render State */}
            {console.log('🔄 Render Product Dropdown - Done:', premiumLookupDone, 'Count:', productsByPremium.length, 'Data:', productsByPremium) || null}

            {premiumLookupDone && productsByPremium.length > 0 ? (
              <>
                <div className="premium-products-hint">
                  📦 พบ {productsByPremium.length} สินค้าที่รองรับเบี้ยจ่ายสุทธิ {formData.net_price?.toLocaleString()} บาท
                </div>
                <select
                  data-dropdown-type="filtered"
                  className={getFieldClassName('product_id')}
                  value={formData.product_id || ''}
                  onChange={(e) => {
                    // Use String comparison to handle both string and number IDs
                    const selectedProduct = productsByPremium.find(p => String(p.id) === e.target.value);
                    console.log('🔍 Product selection - value:', e.target.value, 'found:', selectedProduct);
                    if (selectedProduct) {
                      handleProductSelection(selectedProduct);
                    } else {
                      handleFieldChange('product_id', e.target.value);
                    }
                  }}
                >
                  <option value="">เลือกสินค้า...</option>
                  {/* Sort by number_installment and format display text */}
                  {[...productsByPremium]
                    .sort((a, b) => (a.number_installment || 0) - (b.number_installment || 0))
                    .map(p => {
                      // Check if name contains 'E' (equal payment) or not (30% first payment)
                      const hasE = p.name && p.name.toUpperCase().includes('E');
                      const paymentType = hasE ? 'หารเท่า' : 'งวดแรก 30%';
                      const displayText = `${p.name} (${p.number_installment || '-'} งวด ${paymentType})`;
                      return (
                        <option key={p.id} value={p.id}>
                          {displayText}
                        </option>
                      );
                    })}
                </select>

                {/* Show selected product details */}
                {formData.product_id && formData.product_details && (
                  <div className="product-details-preview">
                    <strong>รายละเอียดสินค้าที่เลือก:</strong>
                    <div className="product-details-grid">
                      <div className="detail-item">
                        <span className="detail-label">ชื่อสินค้า:</span>
                        <span className="detail-value">{formData.product_name || '-'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">ประเภทการชำระ:</span>
                        <span className="detail-value">{formData.product_details.payment_type || '-'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">เบี้ยที่ใช้ค้นหา:</span>
                        <span className="detail-value">{parseFloat(formData.product_details.premium_used_for_lookup || formData.net_price || 0).toLocaleString()} บาท</span>
                      </div>
                      <div className="detail-item highlight">
                        <span className="detail-label">งวดแรก:</span>
                        <span className="detail-value">{parseFloat(formData.product_details.first_payment || 0).toLocaleString()} บาท</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">จำนวนงวด:</span>
                        <span className="detail-value">{formData.product_details.number_installment || '-'} งวด</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">จำนวนงวดจริง:</span>
                        <span className="detail-value">{formData.product_details.real_number_installment || '-'} งวด</span>
                      </div>
                      <div className="detail-item highlight">
                        <span className="detail-label">ยอดผ่อน/งวด:</span>
                        <span className="detail-value">{parseFloat(formData.product_details.payment_installment || 0).toLocaleString()} บาท</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">เปอร์เซ็นต์ดาวน์:</span>
                        <span className="detail-value">{formData.product_details.down_payment_percent || 0}%</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">เปอร์เซ็นต์ค่าบริการ:</span>
                        <span className="detail-value">{formData.product_details.service_percent || 0}%</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">ค่าบริการ:</span>
                        <span className="detail-value">{parseFloat(formData.product_details.service_price || 0).toLocaleString()} บาท</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">ค่าบริการอื่นๆ:</span>
                        <span className="detail-value">{parseFloat(formData.product_details.service_other || 0).toLocaleString()} บาท</span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : premiumLookupDone && productsByPremium.length === 0 ? (
              <>
                <div className="premium-products-warning">
                  ⚠️ ไม่พบสินค้าที่รองรับเบี้ยจ่ายสุทธิ {formData.net_price?.toLocaleString()} บาท - แสดงสินค้าทั้งหมด
                </div>
                <select
                  data-dropdown-type="no-results"
                  className={getFieldClassName('product_id')}
                  value={formData.product_id || ''}
                  onChange={(e) => handleFieldChange('product_id', e.target.value)}
                >
                  <option value="">Select Product...</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </>
            ) : (
              <select
                data-dropdown-type="default"
                className={getFieldClassName('product_id')}
                value={formData.product_id || ''}
                onChange={(e) => handleFieldChange('product_id', e.target.value)}
              >
                <option value="">Select Product...</option>
                {products.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            )}

            {/* Button to refresh products by premium */}
            {formData.net_price && (
              <button
                type="button"
                className="btn-refresh-products"
                onClick={async () => {
                  console.log('🔘 Button clicked - Current state:', { premiumLookupDone, productsCount: productsByPremium.length });
                  await loadProductsByPremium(formData.net_price, true);
                  console.log('🔘 After API call - New state:', { premiumLookupDone, productsCount: productsByPremium.length });
                }}
                disabled={loadingProductsByPremium}
              >
                🔄 ค้นหาสินค้าตามเบี้ย {formData.net_price?.toLocaleString()}
              </button>
            )}
          </div>

          <div className="form-field">
            <label>Price Before VAT (เบี้ยประกันสุทธิ) *</label>
            <input
              className={getFieldClassName('price_before_vat')}
              type="number"
              value={formData.price_before_vat || ''}
              onChange={(e) => handleFieldChange('price_before_vat', e.target.value)}
            />
          </div>

          <div className="form-field">
            <label>Net Price Before (เบี้ยประกันรวม) *</label>
            <input
              className={getFieldClassName('net_price_before')}
              type="number"
              value={formData.net_price_before || ''}
              onChange={(e) => handleFieldChange('net_price_before', e.target.value)}
            />
          </div>

          <div className="form-field">
            <label>Net Price (จ่ายสุทธิ) *</label>
            <input
              className={getFieldClassName('net_price')}
              type="number"
              value={formData.net_price || ''}
              onChange={(e) => handleFieldChange('net_price', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Validation Summary and Save Button */}
      <div className="editor-footer">
        {!isRowComplete && (
          <div className="validation-message">
            ⚠️ Missing required fields ({missingFields.length}): {missingFields.slice(0, 3).join(', ')}
            {missingFields.length > 3 && ` +${missingFields.length - 3} more`}
          </div>
        )}

        {isRowComplete && (
          <div className="success-message">
            ✅ All required fields completed! Ready to save.
          </div>
        )}

        <button
          className={`btn-save ${isRowComplete ? 'ready' : 'disabled'}`}
          disabled={!isRowComplete}
          onClick={() => {
            if (isRowComplete && onSave) {
              console.log('💾 Saving row:', formData);
              onSave({ formData });
              alert('✅ บันทึกเรียบร้อย! ข้อมูลพร้อมสำหรับการส่งไปยัง AME API');
            }
          }}
        >
          {isRowComplete ? '💾 Save Row' : `⏸️ Complete ${missingFields.length} fields to save`}
        </button>
      </div>
    </div>
  );
}

export default PORowEditor;
